import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceArea } from 'recharts';
import styled from 'styled-components';
import { getLabDataByName, updateLabData } from '../services/labService';
import { getProfile, parseProfileForFormula } from '../services/profileService';
import { getMemberLabData } from '../services/memberLabService';
import MainHeader from '../Components/MainHeader';
import CustomAlert from '../Components/CustomAlert';
import { useCustomAlert } from '../hooks/useCustomAlert';
import Hba1cFormula from '../formula/Hba1c';
import FbsFpgFormula from '../formula/FbsFpg';
import NonNpoFormula from '../formula/NonNpo';
import { STATUS_COLORS, getStatusHex, getWorstStatusColor } from '../utils/healthStatusColors';
import { fetchLabDataByNameFallbacks as sharedFetchLabDataByNameFallbacks } from '../utils/labRecordUtils';

const HbA1cContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: 400;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const DateText = styled.div`
  font-size: 14px;
  color: #999;
  font-weight: normal;
  margin-top: 4px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusCard = styled.div`
  background: ${props => props.$bgColor || '#34A853'};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HbA1cIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin: 4px 0;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusLabel = styled.div`
  font-size: 14px;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalItem = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AbnormalName = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalValue = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: white;
  margin: 4px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalStatus = styled.div`
  font-size: 14px;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ChartSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.$color};
`;

const HistorySection = styled.div`
  margin-top: 40px;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const HistoryTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #00BBD3;
  color: white;
`;

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  color: white;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  svg {
    width: 20px;
    height: 20px;
    fill: #34A853;
  }

  &:hover svg {
    fill: #45a049;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SaveButton = styled(Button)`
  background-color: #00BBD3;
  color: white;
`;

const CancelButton = styled(Button)`
  background-color: #e0e0e0;
  color: #666;
`;

const HbA1cPage = () => {
  const navigate = useNavigate();
  const { alertState, showAlert, closeAlert } = useCustomAlert();
  const [loading, setLoading] = useState(true);
  const [fbsData, setFbsData] = useState([]);
  const [hba1cData, setHba1cData] = useState([]);
  const [eagData, setEagData] = useState([]);
  const [nonNpoData, setNonNpoData] = useState([]);
  const [latestDate, setLatestDate] = useState('');
  const [latestValues, setLatestValues] = useState({
    hba1c: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    fbs: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    eag: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray },
    nonNpo: { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray }
  });
  const [profileData, setProfileData] = useState({ age: 0 });
  const [allRecords, setAllRecords] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    fbs: '',
    hba1c: '',
    eag: '',
    nonNpo: ''
  });

  const handleBackClick = () => {
    navigate('/dashboard');
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditForm({
      fbs: record.fbs || '',
      hba1c: record.hba1c || '',
      eag: record.eag || '',
      nonNpo: record.nonNpo || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {

      const updates = [];

      if (editForm.fbs && editForm.fbs !== editingRecord.fbs) {
        updates.push(updateLabData(editingRecord.rawDate, 'fbs', editForm.fbs, 'h'));
      }

      if (editForm.hba1c && editForm.hba1c !== editingRecord.hba1c) {
        updates.push(updateLabData(editingRecord.rawDate, 'hba1c', editForm.hba1c, 'h'));
      }

      if (editForm.eag && editForm.eag !== editingRecord.eag) {
        updates.push(updateLabData(editingRecord.rawDate, 'eag', editForm.eag, 'h'));
      }

      if (editForm.nonNpo && editForm.nonNpo !== editingRecord.nonNpo) {
        updates.push(updateLabData(editingRecord.rawDate, 'non_npo_glucose', editForm.nonNpo, 'h'));
      }

      await Promise.all(updates);

      showAlert('อัปเดตข้อมูลเรียบร้อยแล้ว');

      await loadHbA1cData();
      setShowEditModal(false);
      setEditingRecord(null);
    } catch (error) {
      showAlert('เกิดข้อผิดพลาดในการอัปเดตข้อมูล');
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setEditingRecord(null);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = (date.getFullYear() + 543).toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  const calculateRisk = (hba1c) => {
    const hba1cVal = parseFloat(hba1c) || 0;
    if (!hba1c) return '-';

    if (hba1cVal < 5.7) return 'ปกติ';
    if (hba1cVal >= 5.7 && hba1cVal < 6.5) return 'เสี่ยง';
    return 'ผิดปกติ';
  };

  const formatThaiDate = (dateString) => {
    if (!dateString) return '';

    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const thaiYear = date.getFullYear() + 543;
    const thaiMonth = thaiMonths[date.getMonth()];

    return `${date.getDate()} ${thaiMonth} ${thaiYear}`;
  };

  const loadHbA1cData = async () => {
    try {
      setLoading(true);

      const profileResponse = await getProfile();
      const profile = profileResponse?.data || profileResponse?.profile || {};
      const { age, gender } = parseProfileForFormula(profile);

      setProfileData({ age, gender });

      // ดึงข้อมูลทั้ง 4 ค่า: FBS, HbA1c, eAG, Non-NPO
      const [fbsResponse, hba1cResponse, eagResponse, nonNpoResponse] = await Promise.all([
        sharedFetchLabDataByNameFallbacks(getLabDataByName, ['glucoseresult', 'fbs', 'fbs/fpg']),
        sharedFetchLabDataByNameFallbacks(getLabDataByName, ['diabeteshba1c', 'hba1c', 'hba1chba1c', 'hba1chba1cresult']),
        sharedFetchLabDataByNameFallbacks(getLabDataByName, ['eag', 'hba1ceag', 'hba1ceagresult', 'hba1c eag result', 'hba1c eag']),
        sharedFetchLabDataByNameFallbacks(getLabDataByName, [
          'non_npo_glucose',
          'sugar_non_npo',
          'glucosenonnpc',
          'glucosenonnpcresult',
          'glucose_nonnpc',
          'glucose_nonnpcresult',
          'glucose_non_npc',
          'glucose_non_npcresult',
          'nonnpobloodglucose',
          'nonnpo',
          'diabetesnonnpo',
          'nonnpoglucose',
          'randombloodsugar',
          'rbs'
        ])
      ]);


      // ไม่ filter uploader เพราะ API override เป็น 'm' เสมอ
      // แยกโรงพยาบาล vs ประจำวัน ด้วยชื่อ field แทน
      // fbs/fpg, non_npo_glucose, hba1c = โรงพยาบาล (HospitalCheckupForm)
      // sugar_fasting, sugar_non_npo = ประจำวัน (DailyHealthRecord)
      const sortDesc = (a, b) => new Date(b.dateadd).getTime() - new Date(a.dateadd).getTime();

      let fbsArray = (fbsResponse || []).sort(sortDesc);
      let hba1cArray = (hba1cResponse || []).sort(sortDesc);
      let eagArray = (eagResponse || []).sort(sortDesc);
      let nonNpoArray = (nonNpoResponse || []).sort(sortDesc);


      const convertToDbFormat = (items) =>
        (items || []).map(item => ({ value: item.value, dateadd: item.date, uploader: '' }));

      const mergeUniqueLabItems = (baseItems, extraItems) => {
        const seen = new Set((baseItems || []).map(item => `${item.dateadd || ''}|${item.value ?? ''}`));
        const merged = [...(baseItems || [])];
        (extraItems || []).forEach(item => {
          const key = `${item.dateadd || ''}|${item.value ?? ''}`;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(item);
          }
        });
        return merged.sort(sortDesc);
      };

      const getMemberLabItems = (labData, keys) => {
        const collected = [];
        keys.forEach((key) => {
          if (labData?.[key]?.length > 0) {
            collected.push(...labData[key]);
          }
        });
        return collected;
      };

      // Fallback: รวม key จากสมุดสุขภาพของ admin ให้ครบทุก alias
      if (fbsArray.length === 0 || hba1cArray.length === 0 || eagArray.length === 0 || nonNpoArray.length === 0) {
        try {
          const memberLabData = await getMemberLabData();
          const labData = memberLabData?.labData;
          if (labData) {
            const fbsFallback = convertToDbFormat(
              getMemberLabItems(labData, ['glucoseresult', 'fbs', 'fbs/fpg'])
            );
            const hba1cFallback = convertToDbFormat(
              getMemberLabItems(labData, ['diabeteshba1c', 'hba1c', 'hba1chba1c', 'hba1chba1cresult'])
            );
            const eagFallback = convertToDbFormat(
              getMemberLabItems(labData, ['eag', 'hba1ceag', 'hba1ceagresult', 'hba1c eag result', 'hba1c eag'])
            );
            const nonNpoFallback = convertToDbFormat(
              getMemberLabItems(labData, [
                'non_npo_glucose',
                'sugar_non_npo',
                'glucosenonnpc',
                'glucosenonnpcresult',
                'glucose_nonnpc',
                'glucose_nonnpcresult',
                'glucose_non_npc',
                'glucose_non_npcresult',
                'nonnpobloodglucose',
                'nonnpo',
                'diabetesnonnpo',
                'nonnpoglucose',
                'randombloodsugar',
                'rbs'
              ])
            );

            fbsArray = mergeUniqueLabItems(fbsArray, fbsFallback);
            hba1cArray = mergeUniqueLabItems(hba1cArray, hba1cFallback);
            eagArray = mergeUniqueLabItems(eagArray, eagFallback);
            nonNpoArray = mergeUniqueLabItems(nonNpoArray, nonNpoFallback);
          }
        } catch (e) {
        }
      }


      // Process FBS data
      const processedFbs = fbsArray.map(item => ({
        date: item.dateadd?.split('T')[0].split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      // Process HbA1c data
      const processedHbA1c = hba1cArray.map(item => ({
        date: item.dateadd?.split('T')[0].split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      // Process eAG data
      const processedEag = eagArray.map(item => ({
        date: item.dateadd?.split('T')[0].split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      // Process Non-NPO data
      const processedNonNpo = nonNpoArray.map(item => ({
        date: item.dateadd?.split('T')[0].split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      setFbsData(processedFbs);
      setHba1cData(processedHbA1c);
      setEagData(processedEag);
      setNonNpoData(processedNonNpo);

      // Combine records for history table
      const recordsMap = new Map();

      fbsArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, fbs: null, hba1c: null, eag: null, nonNpo: null });
        }
        // เก็บแค่ค่าแรก (sort desc = ล่าสุด) ไม่ overwrite
        if (recordsMap.get(date).fbs === null) recordsMap.get(date).fbs = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      hba1cArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, fbs: null, hba1c: null, eag: null, nonNpo: null });
        }
        if (recordsMap.get(date).hba1c === null) recordsMap.get(date).hba1c = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      eagArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, fbs: null, hba1c: null, eag: null, nonNpo: null });
        }
        if (recordsMap.get(date).eag === null) recordsMap.get(date).eag = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      nonNpoArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, fbs: null, hba1c: null, eag: null, nonNpo: null });
        }
        if (recordsMap.get(date).nonNpo === null) recordsMap.get(date).nonNpo = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      const combinedRecords = Array.from(recordsMap.values()).sort((a, b) =>
        new Date(b.date) - new Date(a.date)
      );

      setAllRecords(combinedRecords);

      const latestRecord = combinedRecords[0] || null;
      if (latestRecord) {
        setLatestDate(formatThaiDate(latestRecord.rawDate || latestRecord.date));
      }

      // Calculate all values
      const colorMap = STATUS_COLORS;

      // HbA1c from latest checkup day only
      let hba1cStatus = { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray };
      if (latestRecord?.hba1c !== null && latestRecord?.hba1c !== undefined && latestRecord?.hba1c !== '') {
        const hba1cValue = parseFloat(latestRecord.hba1c);
        if (Number.isFinite(hba1cValue)) {
        const hba1cResult = await Hba1cFormula(hba1cValue, age, gender);
        // ปิด logic สีตามค่าผลตรวจ (แดง/เขียว) — บังคับให้เป็นสีเทาทั้งหมดทุกหมวด
        hba1cStatus = {
          value: `${latestRecord.hba1c} %`,
          status: hba1cResult?.title || 'ปกติ',
          // color: hba1cResult ? (colorMap[hba1cResult.color] || STATUS_COLORS.green) : STATUS_COLORS.green,
          color: STATUS_COLORS.gray,
          rawValue: hba1cValue
        };
        } else {
          hba1cStatus = {
            value: String(latestRecord.hba1c),
            status: String(latestRecord.hba1c),
            color: STATUS_COLORS.gray,
            rawValue: latestRecord.hba1c
          };
        }
      }

      // FBS from latest checkup day only
      let fbsStatus = { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray };
      if (latestRecord?.fbs !== null && latestRecord?.fbs !== undefined && latestRecord?.fbs !== '') {
        const fbsValue = parseFloat(latestRecord.fbs);
        if (Number.isFinite(fbsValue)) {
          const fbsResult = await FbsFpgFormula(fbsValue);
          // ปิด logic สีตามค่าผลตรวจ (แดง/เขียว) — บังคับให้เป็นสีเทาทั้งหมดทุกหมวด
          fbsStatus = {
            value: `${latestRecord.fbs} mg/dL`,
            status: fbsResult?.title || 'ปกติ',
            // color: fbsResult ? (colorMap[fbsResult.color] || STATUS_COLORS.green) : STATUS_COLORS.green,
            color: STATUS_COLORS.gray,
            rawValue: fbsValue
          };
        } else {
          fbsStatus = {
            value: String(latestRecord.fbs),
            status: String(latestRecord.fbs),
            color: STATUS_COLORS.gray,
            rawValue: latestRecord.fbs
          };
        }
      }

      // eAG from latest checkup day only
      let eagStatus = { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray };
      if (latestRecord?.eag !== null && latestRecord?.eag !== undefined && latestRecord?.eag !== '') {
        const eagValue = parseFloat(latestRecord.eag);
        if (Number.isFinite(eagValue)) {
          const eagStatusText = eagValue < 70 ? 'ต่ำ' : eagValue > 154 ? 'สูง' : 'ปกติ';
          // ปิด logic สีตามค่าผลตรวจ (แดง/เขียว) — บังคับให้เป็นสีเทาทั้งหมดทุกหมวด
          eagStatus = {
            value: `${latestRecord.eag} mg/dL`,
            status: eagStatusText,
            // color: eagStatusText === 'ปกติ' ? STATUS_COLORS.green : STATUS_COLORS.red,
            color: STATUS_COLORS.gray,
            rawValue: eagValue
          };
        } else {
          eagStatus = {
            value: String(latestRecord.eag),
            status: String(latestRecord.eag),
            color: STATUS_COLORS.gray,
            rawValue: latestRecord.eag
          };
        }
      }

      // Non-NPO from latest checkup day only
      let nonNpoStatus = { value: '-', status: 'ไม่มีข้อมูล', color: STATUS_COLORS.gray };
      if (latestRecord?.nonNpo !== null && latestRecord?.nonNpo !== undefined && latestRecord?.nonNpo !== '') {
        const nonNpoValue = parseFloat(latestRecord.nonNpo);
        if (Number.isFinite(nonNpoValue)) {
          const nonNpoResult = await NonNpoFormula(nonNpoValue);
          // ปิด logic สีตามค่าผลตรวจ (แดง/เขียว) — บังคับให้เป็นสีเทาทั้งหมดทุกหมวด
          nonNpoStatus = {
            value: `${latestRecord.nonNpo} mg/dL`,
            status: nonNpoResult?.title || 'ปกติ',
            // color: nonNpoResult ? (colorMap[nonNpoResult.color] || STATUS_COLORS.green) : STATUS_COLORS.green,
            color: STATUS_COLORS.gray,
            rawValue: nonNpoValue
          };
        } else {
          nonNpoStatus = {
            value: String(latestRecord.nonNpo),
            status: String(latestRecord.nonNpo),
            color: STATUS_COLORS.gray,
            rawValue: latestRecord.nonNpo
          };
        }
      }

      setLatestValues({
        hba1c: hba1cStatus,
        fbs: fbsStatus,
        eag: eagStatus,
        nonNpo: nonNpoStatus
      });

    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHbA1cData();
  }, []);

  return (
    <HbA1cContainer>
      <MainHeader />

      <TopBar>น้ำตาลในเลือด</TopBar>

      <Header>
        <BackButton onClick={handleBack}>
          &lt; ย้อนกลับ
        </BackButton>
        <PageTitle style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <HbA1cIcon src="./น้ำตาลสี.png" alt="น้ำตาลสะสม" style={{ width: '32px', height: '32px' }} />
          น้ำตาลในเลือด (ตรวจจากโรงพยาบาล)
        </PageTitle>
        {latestDate && (
          <DateText>บันทึกล่าสุดวันที่ {latestDate}</DateText>
        )}
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : (
          <>
            {/* Status Card */}
            {/* ปิด logic การตัดสินสี/สถานะ (แดง/เขียว/ผิดปกติ) ทั้งหมด — เหลือแค่ไอคอน + ชื่อหมวดเท่านั้น */}
            {/* Status Card — comment ทั้งการ์ดออก เพราะย้ายไอคอนไปไว้ที่ Header ด้านบนแล้ว */}
            {/* {(() => {
              // const fields = [
              //   latestValues.hba1c,
              //   latestValues.fbs,
              //   latestValues.eag,
              //   latestValues.nonNpo
              // ];
              // const hasData = fields.some((item) => item?.value && item.value !== '-');
              // const hasAbnormal = fields.some((item) => item?.color === STATUS_COLORS.red);
              // const hasKnownStatus = fields.some((item) => item?.color === STATUS_COLORS.green || item?.color === STATUS_COLORS.red);
              // const cardColor = getWorstStatusColor(fields.map((item) => item?.color));
              const cardColor = STATUS_COLORS.gray;

              return (
                <StatusCard $bgColor={cardColor}>
                  <HbA1cIcon src="./น้ำตาลสี.png" alt="น้ำตาลสะสม" />
                  <StatusInfo>
                    <StatusTitle style={{ color: 'white' }}>น้ำตาลในเลือด</StatusTitle>
                    {!hasData ? (
                      <StatusTitle style={{ color: 'white' }}>ไม่มีข้อมูล</StatusTitle>
                    ) : hasKnownStatus && !hasAbnormal ? (
                      <StatusTitle style={{ color: 'white' }}>ผลตรวจปกติ</StatusTitle>
                    ) : !hasKnownStatus ? (
                      <StatusTitle style={{ color: 'white' }}>ไม่มีข้อมูลที่แปลผลได้</StatusTitle>
                    ) : (
                      <>
                        {latestValues.hba1c?.color === STATUS_COLORS.red && (
                          <AbnormalItem>
                            <AbnormalName>HbA1c</AbnormalName>
                            <AbnormalValue>{latestValues.hba1c.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.hba1c.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {latestValues.fbs?.color === STATUS_COLORS.red && (
                          <AbnormalItem>
                            <AbnormalName>FBS (อดอาหาร)</AbnormalName>
                            <AbnormalValue>{latestValues.fbs.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.fbs.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {latestValues.eag?.color === STATUS_COLORS.red && (
                          <AbnormalItem>
                            <AbnormalName>eAG</AbnormalName>
                            <AbnormalValue>{latestValues.eag.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.eag.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {latestValues.nonNpo?.color === STATUS_COLORS.red && (
                          <AbnormalItem>
                            <AbnormalName>Non-NPO (ไม่อดอาหาร)</AbnormalName>
                            <AbnormalValue>{latestValues.nonNpo.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.nonNpo.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                      </>
                    )}
                  </StatusInfo>
                </StatusCard>
              );
            })()} */}

            {/* FBS Chart */}
            <ChartSection>
              <ChartTitle>น้ำตาลในเลือดขณะอดอาหาร (FBS)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (70-99 mg/dL)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ผิดปกติ</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={fbsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    domain={[0, 200]}
                    tick={{ fontSize: 12 }}
                  />
                  <ReferenceArea y1={70} y2={99} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={0} y2={70} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={99} y2={200} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FF5722"
                    strokeWidth={2}
                    dot={{ fill: '#FF5722', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* HbA1c Chart */}
            <ChartSection>
              <ChartTitle>น้ำตาลสะสม (HbA1c)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (&lt; 5.7%)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFF3E0" />
                  <span>เสี่ยง (5.7-6.4%)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>สูง (≥ 6.5%)</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={hba1cData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    domain={[0, 10]}
                    ticks={[0, 2, 4, 5.7, 6.5, 8, 10]}
                    tick={{ fontSize: 12 }}
                  />
                  <ReferenceArea y1={0} y2={5.7} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={5.7} y2={6.5} fill="#FFF3E0" fillOpacity={0.6} />
                  <ReferenceArea y1={6.5} y2={10} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FF5722"
                    strokeWidth={2}
                    dot={{ fill: '#FF5722', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* eAG Chart */}
            <ChartSection>
              <ChartTitle>eAG (Estimated Average Glucose)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (70-154 mg/dL)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ผิดปกติ</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={eagData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    domain={[0, 300]}
                    tick={{ fontSize: 12 }}
                  />
                  <ReferenceArea y1={70} y2={154} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={0} y2={70} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={154} y2={300} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FF5722"
                    strokeWidth={2}
                    dot={{ fill: '#FF5722', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* Non-NPO Chart */}
            <ChartSection>
              <ChartTitle>น้ำตาลในเลือดไม่อดอาหาร (Non-NPO)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#E8F5E9" />
                  <span>ปกติ (70-99 mg/dL)</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFEBEE" />
                  <span>ผิดปกติ</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={nonNpoData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    domain={[0, 200]}
                    tick={{ fontSize: 12 }}
                  />
                  <ReferenceArea y1={70} y2={99} fill="#E8F5E9" fillOpacity={0.6} />
                  <ReferenceArea y1={0} y2={70} fill="#FFEBEE" fillOpacity={0.6} />
                  <ReferenceArea y1={99} y2={200} fill="#FFEBEE" fillOpacity={0.6} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FF5722"
                    strokeWidth={2}
                    dot={{ fill: '#FF5722', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            <HistorySection>
              <HistoryTitle>ประวัติการตรวจ</HistoryTitle>
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>วันที่ตรวจ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่วัด</TableHeaderCell>
                    <TableHeaderCell>ความเสี่ยง</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {allRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                        ไม่พบข้อมูล
                      </TableCell>
                    </TableRow>
                  ) : (
                    allRecords.map((record, index) => {
                      const values = [];
                      if (record.fbs) values.push(`FBS: ${record.fbs} mg/dL`);
                      if (record.hba1c) values.push(`HbA1c: ${record.hba1c}%`);
                      if (record.eag) values.push(`eAG: ${record.eag} mg/dL`);
                      if (record.nonNpo) values.push(`Non-NPO: ${record.nonNpo} mg/dL`);

                      return (
                        <TableRow key={index}>
                          <TableCell>{formatDisplayDate(record.date)}</TableCell>
                          <TableCell>{values.join(', ') || '-'}</TableCell>
                          <TableCell>{calculateRisk(record.hba1c)}</TableCell>
                        <TableCell>
                          {/* ปิดการแก้ไขข้อมูลจากตารางประวัติ — comment ไอคอนแก้ไขไว้ ไม่ลบ */}
                          {/* <EditButton onClick={() => handleEditClick(record)}>
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                            </svg>
                          </EditButton> */}
                        </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </HistorySection>
          </>
        )}
      </Content>

      {showEditModal && editingRecord && (
        <Modal onClick={handleModalClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>แก้ไขข้อมูล</ModalTitle>

            <FormGroup>
              <Label>วันที่: {formatDisplayDate(editingRecord.date)}</Label>
            </FormGroup>

            <FormGroup>
              <Label>น้ำตาลขณะอดอาหาร (FBS) (mg/dL)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.fbs}
                onChange={(e) => setEditForm({ ...editForm, fbs: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>น้ำตาลสะสม (HbA1c) (%)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.hba1c}
                onChange={(e) => setEditForm({ ...editForm, hba1c: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>eAG (mg/dL)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.eag}
                onChange={(e) => setEditForm({ ...editForm, eag: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>น้ำตาลไม่อดอาหาร (Non-NPO) (mg/dL)</Label>
              <Input
                type="text"
                step="0.1"
                value={editForm.nonNpo}
                onChange={(e) => setEditForm({ ...editForm, nonNpo: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <ButtonGroup>
              <CancelButton onClick={handleModalClose}>ยกเลิก</CancelButton>
              <SaveButton onClick={handleEditSubmit}>บันทึก</SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {alertState.isOpen && (
        <CustomAlert message={alertState.message} onClose={closeAlert} />
      )}
    </HbA1cContainer>
  );
};

export default HbA1cPage;
